<template>
  <div id="reception">
    <template v-if="ready">

      <div class="tabs-wrapper">
        <div class="tabs">
          <div class="tab" v-for="tab of tabs" :class="{active: activeTab === tab}" @click="setActiveTab(tab)">
            {{$t(`deliveryMethod.${tab}`)}}
            <div class="count" v-if="salesByTab[tab] && salesByTab[tab].length > 0">
              {{salesByTab[tab].length}}
            </div>
          </div>
          <div class="tab" :class="{active: isHistory}" @click="setActiveTab('history')">{{$t('sales.history')}}</div>
          <div class="tab filler"></div>
        </div>
      </div>

      <div class="sales" v-if="activeTab">
        <div class="empty" v-if="!isHistory && (!salesByTab[activeTab] || salesByTab[activeTab].length === 0)">{{$t('sales.no_sales')}}</div>
        <div class="sale" v-for="sale of (isHistory ? historySales : salesByTab[activeTab])" :key="`sale_${sale.id}`">
          <div class="header">
            <div class="section sale">
              <div class="sale-number">
                <template v-if="sale.fromPosModule && sale.invoices.length > 0">
                  {{$t('sales.invoices')}} {{sale.invoices.map(i => "#" + i).join(", ")}}
                </template>
                <template v-else>
                  {{$t('sales.sale')}} #{{sale.id}}
                </template>
              </div>
              <div class="method">{{sale.method !== 'none' ? $t(`deliveryMethod.${sale.method}`) : (sale.fromPosModule ? $t(`deliveryMethod.payAtTheTable`) : '')}}</div>
              <div class="more">
                <button class="btn expand" @click="toggleExpand(sale)">{{sale.expanded ? '▲' : '▼'}}</button>
                <button class="btn" @click="showSelectedSale(sale)">
                  {{$t('sales.details')}}
                  <svgicon src="icon/forward.svg" :height="12" :width="15"></svgicon>
                </button>
              </div>
            </div>
            <div class="section dates">
              <div class="info">
                <label>{{$t('sales.ordered_on')}}</label>
                <div class="date">{{formatDate(sale.finishAt)}}</div>
              </div>
              <div class="info">
                <label>{{$t('sales.in_advance_for')}}</label>
                <div class="date">{{formatDate(sale.expectedDeliveryTime)}}</div>
              </div>
              <div class="info" v-if="$route.query.branch && !$route.query.company">
                <label>{{$t('company_single')}}</label>
                <div class="company">{{sale.companyId}}</div>
              </div>
            </div>
            <div class="section customer">
              <div class="info">
                <label>{{$t('customer.title')}}</label>
                <div class="name">{{sale.contact.name}}</div>
                <template v-if="sale.method === 'delivery' && sale.contact.address">
                  <div class="address">{{sale.contact.address}}</div>
                  <div class="address" v-if="sale.contact.app">{{sale.contact.app}}</div>
                </template>
                <div class="email">{{sale.contact.email}}</div>
                <div class="phone">{{sale.contact.phone}}</div>
              </div>
              <div class="info" v-if="sale.table && sale.fromPosModule">
                <label>{{$t('test_sale.table')}}</label>
                <div class="table">{{sale.table}}</div>
              </div>
            </div>
            <div class="section last">
              <div class="info">
                <label>{{$t('sales.amount')}}</label>
                <div class="amount">{{$tc(sale.total, sale.currency)}}</div>
              </div>
              <div class="info">
                <label>{{$t('sales.status')}}</label>
                <div class="status" :class="getStatusForSale(sale)">{{$t('sales.status_' + getStatusForSale(sale))}}</div>
              </div>
            </div>
            <div class="section action">
              <template v-if="!isHistory">
                <div class="confirm" @click="updateStatus(sale)" :class="{confirmed: getStatusForSale(sale) === 'confirmed'}">
                  <svgicon src="icon/check.svg" :height="25" :width="25"></svgicon>
                </div>
                <div class="close" @click="updateStatus(sale)" :class="{show: getStatusForSale(sale) === 'confirmed'}">
                  <svgicon src="icon/close.svg" :height="15" :width="15"></svgicon>
                </div>
              </template>
            </div>
          </div>

          <SaleDetails class="section details" v-if="sale.expanded"  :sale="sale"/>

          <div class="sale__warning" v-if="sale.posFailed">
            <svgicon src="icon/danger.svg" :height="20" :width="20"></svgicon>
            <span class="warning-text" v-html="$t('sales.pos_failed')"></span>
            <button class="btn btn-primary" @click="resendToPos(sale)">{{$t('sales.resend_pos.resend')}}</button>
          </div>
        </div>
      </div>

      <Modal name="sale-details" :title="$t('sales.order_details') + ' <span>#' + (selectedSale ? selectedSale.id : '') + '</span>'"
             :actions="[{name: $t('back'), action: 'close'}]">
        <div class="content" v-if="selectedSale">
          <SaleDetails class="items" :sale="selectedSale"/>
        </div>
      </Modal>

      <div class="sound-configuration" :class="{on: soundConfig}" @click="toggleSoundConfig">
        <div class="icon">
          <svgicon v-if="soundConfig" src="icon/sound-on.svg" :height="16" :width="16"></svgicon>
          <svgicon v-if="!soundConfig" src="icon/sound-mute.svg" :height="16" :width="16"></svgicon>
        </div>
        <div class="text">
          <template v-if="soundConfig">{{$t('sound_enabled')}}</template>
          <template v-if="!soundConfig">{{$t('sound_disabled')}}</template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Core } from "@/core/Core";
import { mapActions } from "vuex";
import { DateTime } from "luxon";
import SaleDetails from "@/components/reception/SaleDetails.vue";


export default {
  name: "reception",

  components: { SaleDetails },

  data() {
    return {
      sound: null,
      COOKIE_NAME: "enable-sound",
      soundDuration: 15, //seconds
      activeTab: null,
      company: null,
      companies: [],
      selectedSale: null,
      enableSound: 'no_configuration',
      historySales: [],
      ready: false,
      retryMaitreDFailedSale: false
    }
  },

  computed: {
    isHistory() {
      return this.activeTab === 'history';
    },
    sales() {
      return this.$store.state.sales;
    },
    isBranchUser() {
      return this.$route.query.branch && !this.$route.query.company;
    },
    soundConfig() {
      switch (this.enableSound) {
        case "yes":
          return true;
        case "no":
          return false;
        default:
        case "no_configuration": //if not set in cookie, then disabled by default
          return false;
      }
    },
    tabs() {
      let tabs = Object.keys(this.salesByTab);
      if (this.activeTab && this.activeTab !== 'history' && !tabs.includes(this.activeTab)) {
        tabs.push(this.activeTab);
      }
      return tabs;
    },
    salesByTab() {
      let tabs = {};
      let methods = ['inPlace', 'takeout', 'delivery'];
      let activatedMethods = [];
      let salesMethods = this.getSalesMethods().filter(m => methods.includes(m));

      /* Branch level methods */
      if (this.isBranchUser) {
        activatedMethods = this.companies.map(c => methods.filter(m =>
            c.onlineOrdering && c.onlineOrdering.activated && c.onlineOrdering[m] && c.onlineOrdering[m].activated));
        activatedMethods = [...new Set(activatedMethods.flat())]; //Merge & remove duplicates
        if (activatedMethods.length > 0 || salesMethods.length > activatedMethods.length) {
          tabs['all'] = [];
        }
        let atLeastOneCompanyWithQrPay = this.companies.filter(c => c.payAtTheTable &&  c.payAtTheTable.activated);
        if (atLeastOneCompanyWithQrPay) {
          tabs['payAtTheTable'] = [];
        }
        for (let method of activatedMethods) {
          tabs[method] = [];
        }

        /* Company level methods */
      } else if (this.company) {
        if (this.company.onlineOrdering && this.company.onlineOrdering.activated) {
          activatedMethods = methods.filter(m => this.company.onlineOrdering[m] && this.company.onlineOrdering[m].activated);
        }
        if (activatedMethods.length > 0 || salesMethods.length > activatedMethods.length) {
          tabs['all'] = [];
        }
        if (this.company.payAtTheTable && this.company.payAtTheTable.activated) {
          tabs['payAtTheTable'] = [];
        }
        for (let method of activatedMethods) {
          tabs[method] = [];
        }
      }

      // Reorder sales in category
      for (let sale of this.sales) {
        if (tabs['all']) {
          tabs['all'].push(sale);
        }
        let addToTabName = sale.fromPosModule ? 'payAtTheTable' : sale.method;
        if (!tabs[addToTabName]) {
          tabs[addToTabName] = [];
        }
        tabs[addToTabName].push(sale);
      }

      return tabs;
    }
  },

  watch: {
    salesByTab() {
      if (!this.activeTab && Object.keys(this.salesByTab).length && this.ready) {
        this.activeTab = Object.keys(this.salesByTab)[0];
      }
    },
    activeTab() {
      if (this.isHistory) {
        this.fetchHistory();
      }
    },
    sales: {
      deep: true,
      handler() {
        this.playSound();
        let sales = this.salesByTab;
      }
    },
  },

  async mounted() {
    this.sound = new Audio(require('@/assets/sound/notification.mp3'));
    this.$store.state.pageTitleTranslationKey = "reception.title";
    this.$store.state.pageIcon = "/icon/reception.svg";

    showSpinner();
    this.setSoundConfig();
    if (this.isBranchUser) {
      await this.loadCompanies();
    } else {
      this.company = await this.$store.getters.getContextCompany;
    }

    this.ready = true;

    hideSpinner();
  },

  beforeDestroy() {
    this.sound.loop = false;
  },

  methods: {
    ...mapActions(["screenPendingSales", "screenConfirmSale", 'screenSaleHistory', 'getCompanies', "getCompanyExternalPos"]),

    playSound() {
      if (!this.sound || !this.soundConfig) {
        return;
      }
      this.sound.play();
      this.sound.loop = true;
      setTimeout(() => {
        this.sound.loop = false;
      }, this.soundDuration * 1000);
    },
    toggleSoundConfig() {
      if (this.sound.loop && this.soundConfig) {
        this.sound.loop = false;
      }
      setCookie(this.COOKIE_NAME, this.soundConfig ? 'no' : 'yes');
      this.setSoundConfig();
    },
    setSoundConfig() {
      if (getCookie(this.COOKIE_NAME)) {
        this.enableSound = getCookie(this.COOKIE_NAME);
      }
    },

    showSelectedSale(sale) {
      this.selectedSale = sale;
      this.$modal.show("sale-details");
    },

    toggleExpand(sale) {
      sale.expanded = !sale.expanded;
      this.$forceUpdate();
    },

    async updateStatus(sale) {
      if (this.sound.loop) {
        this.sound.loop = false;
      }
      if (!sale.status) {
        let response = await this.screenConfirmSale(sale.id);
        if (response) {
          sale.status = 'confirmed';
        } else {
          toast(this.$t("error.no_server"), "error");
        }
        this.$forceUpdate();
        return;
      }
      //visual not updating
      if (sale.status === 'confirmed') {
        let index = this.sales.indexOf(sale);
        this.sales.splice(index, 1);
      }
    },

    async loadCompanies() {
      let response = await this.getCompanies(this.$route.query.branch);
      if (response) {
        this.companies = response;
      }
    },

    async fetchHistory() {
      this.$store.commit('showSpinner', true);
      this.historySales = [];
      let response = await this.screenSaleHistory({offset: 0, perPage: 20});
      this.$store.commit('showSpinner', false);

      this.historySales = response.data;
      for (let sale of this.historySales) {
        sale.status = 'confirmed';
      }
    },

    getStatusForSale(sale) {
      if (!sale.status) {
        return "pending";
      }
      return sale.status;
    },

    formatDate(dateString) {
      if (!dateString) {
        return '';
      }
      if (dateString && dateString.date) {
        dateString = dateString.date;
      }
      let luxonDate = DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss.000000');
      return luxonDate.invalid ? dateString : luxonDate.toLocaleString(DateTime.DATETIME_MED);
    },



    async resendToPos(sale) {
      let response = await showConfirmation(this.$t("warning"), this.$t("sales.resend_pos.confirmation"));
      if (!response) {
        return;
      }
      showSpinner();
      try {
        let response = await Core.request('POST', `/api/sale/${sale.id}/retry-hold-and-fire`)
        hideSpinner();
        if (response && !response.failed) {
          sale.posFailed = false;
          this.$forceUpdate();
          toast(this.$t("sales.resend_pos.success"), "success");
        } else {
          toast(this.$t("sales.resend_pos.failed"), "error", this.$t("error.title"));
          if (response.error) {
            toast(response.error, "error", this.$t("error.title"));
          }
        }
      } catch(e) {
        hideSpinner();
        toast(this.$t("error.no_server"), "error", this.$t("error.title"));
        console.log(e)
      }
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },

    getSalesMethods() {
      let methods = this.sales.map(s => s.fromPosModule ? 'payAtTheTable' : s.method);
      return [...new Set(methods.flat())];
    }
  }
}
</script>

<style lang="scss" scoped>
  .tabs {
    flex-wrap: nowrap;
  }
  .tabs .tab {
    flex-grow: 0;
    white-space: nowrap;
    padding: 15px 18px 13px 18px;
    min-width: 230px;
    position: relative;

    &.filler {
      flex-grow: 1;
      pointer-events: none;
    }
  }

  .count {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 5px;
    background-color: var(--primary-color);
    min-width: 20px;
    height: 20px;
    padding: 5px;
    border-radius: 15px;
    color: white;
    font-weight: bold;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty {
    text-align: center;
    color: #888;
    margin-top: 40px;
    font-size: 14px;
  }

  .sale {
    background-color: white;
    padding-bottom: 25px;
    font-size: 13px;
    margin-left: auto;
    margin-right: auto;
    max-width: 850px;
    border-radius: 6px;
    width: 100%;
    margin-top: 20px;

    label {
      font-size: 13px;
    }

    .header {
      display: flex;
      flex-wrap: wrap;
    }

    .expand {
      margin-right: 5px;
    }

    .btn {
      font-size: 12px;
      display: flex;
      color: #fc6650;
      fill: #fc6650;
      padding: 6px 12px;
      background-color: #f5f5f5;
      margin-top: 15px;

      .svgicon {
        margin-left: 10px;
      }
    }
  }

  .info:not(:last-child) {
    margin-bottom: 10px;
  }

  .phone {
    margin-top: 6px;
  }

  .email {
    margin-top: 6px;
  }

  .section {
    padding: 0 15px;
    margin-top: 25px;
    flex-basis: 0;
    flex-grow: 1;

    &.details {
      margin-top: 0;
    }

    &:not(:last-child):not(.last) {
      border-right: 1px solid #f1f1f1;
    }

    &.action {
      flex-grow: 0;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-right: 25px;
    }
  }

  .confirm, .close {
    border-radius: 100%;
    background-color: #f1f1f1;
    display: flex;
    align-items: center;
    justify-content: center;
    fill: #888;
    cursor: pointer;
  }

  .close {
    width: 35px;
    margin-top: -10px;
    pointer-events: none;
    opacity: 0;
    height: 0;
    transition: opacity 0.3s ease;

    &.show {
      opacity: 1;
      height: 35px;
      pointer-events: all;
    }
  }

  .confirm {
    width: 45px;
    height: 45px;
    transition: background-color 0.3s ease;

    &:hover, &.confirmed {
      background-color: #2ab152;
      fill: white;
    }

    .svgicon {
      transition: fill 0.3s ease;
    }
  }

  .more {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sale-number {
    font-weight: bold;
    font-size: 18px;
    text-align: center;
    margin-bottom: 4px;
  }
  .method {
    text-align: center;
    font-weight: bold;
  }

  .sound-configuration {
    position: absolute;
    bottom: 15px;
    right: 30px;
    display: flex;
    background-color: #e6e6e6;
    border-radius: 6px;
    padding: 10px;
    cursor: pointer;

    .icon {
      margin-right: 8px;
    }

    .text {
      font-size: 13px;
      font-weight: 500;
    }
  }

  @media screen and (max-width: 1100px) {
    .sound-configuration {
      bottom: auto;
      top: 95px;
    }
  }

  @media screen and (max-width: 600px) {
    .sound-configuration {
      right: 10px;

      .icon {
        margin-right: 0;
      }
      .text {
        display: none;
      }
    }

    .tabs-wrapper {
      position: relative;
      display: flex;
      &::after {
        content:" ";
        position: absolute;
        right: -2px;
        background-image: linear-gradient(to right, rgba(242,242,242,0), rgba(242,242,242,1));
        height: 59px;
        width: 50px;
      }
    }

    .tabs {
      flex-wrap: nowrap;
      overflow-x: scroll;
      position: relative;
    }
    .tabs .tab {
      flex-grow: 1;
      //white-space: nowrap;
      padding: 15px 33px 13px 18px;
      min-width: auto;
      position: relative;

      &.filler {
        display: none;
      }
    }

    .section {
      flex-basis: 50%;
      &.sale {
        order: -3;
      }
      &.last {
        order: -2;
        flex-basis: 30%;
      }
      &.action {
        order: -1;
        padding-left: 0;
        flex-basis: 20%;
      }
      &.customer {
        border-right: none !important;
      }
    }
  }

  .sale__warning {
    $color: #ed2937;
    display: flex;
    border-radius: 8px;
    fill: white;
    color: white;
    background-color: $color;
    align-items: center;
    padding: 10px 20px;
    margin: 20px 20px 0 20px;

    .warning-text {
      margin-left: 20px;
      flex-grow: 1;
    }

    .btn {
      margin-top: 0;
      color: $color;
      background-color: white;
      margin-left: 20px;
    }
  }
</style>

<style lang="scss">
  #reception .modal {
    z-index: 10;
    .modalContainer {
      width: 450px;
    }
    .title {
      display: flex;
      font-size: 16px;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }
    .title span {
      color: var(--primary-color);
      margin-left: 10px;
    }
  }

  #reception .details {
    .section {
      display: flex;
      flex-wrap: wrap;
      margin-top: 20px;
    }
    .item {
      width: calc(50% - 20px);
      margin-right: 10px;
      margin-bottom: 8px !important;
    }
  }

  @media screen and (max-width: 720px) {
    #reception .modal {
      .modalContainer {
        padding: 20px 10px;
      }
    }
  }

  @media screen and (max-width: 600px) {
    #reception {
      .details .item {
        flex-basis: 100%;
      }

      .modalContainer {
        margin: 50px 20px;
      }
    }

  }
</style>
